import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  CheckCircle,
  ClipboardList,
  Clock,
  Layers,
  Phone,
  RefreshCw,
  Server,
  Shield,
  Target,
  Timer,
  Users,
  XCircle,
} from 'lucide-react';
import { motion } from 'motion/react';

import resilienceBcrpDashboard from '@src/assets/screenshots/resilience-bcrp-dashboard.webp';
import resilienceContinuityDetail from '@src/assets/screenshots/resilience-continuity-detail.webp';
import resilienceContinuity from '@src/assets/screenshots/resilience-continuity.webp';
import resilienceRecoveryDetail from '@src/assets/screenshots/resilience-recovery-detail.webp';
import resilienceRecovery from '@src/assets/screenshots/resilience-recovery.webp';
import { AIAgentBanner } from '@src/components/AIAgentBanner';
import { ScreenshotGallery } from '@src/components/ScreenshotGallery';
import AnswerSection from '@src/components/seo/AnswerSection';
import { Button } from '@src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@src/components/ui/card';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

const TEAL = '#0d9488';
const BG_COLOR = '#F8FAFC';

// Pain points for Business Continuity & Disaster Recovery
const painPoints = [
  {
    icon: XCircle,
    problem: 'No Recovery Plans',
    description: 'When disaster strikes, teams scramble with no documented procedures or clear ownership.',
    solution: 'Comprehensive BCP and DR plans with clear ownership, procedures, and automated workflows.',
  },
  {
    icon: Timer,
    problem: 'Unknown Recovery Time',
    description: 'RTO/RPO targets exist on paper but have never been tested or validated.',
    solution: 'Defined RTO/RPO targets with regular testing, tracking, and gap analysis.',
  },
  {
    icon: AlertTriangle,
    problem: 'Critical Functions Unknown',
    description: 'No clarity on which business processes are truly critical or their dependencies.',
    solution: 'Business Impact Analysis (BIA) with criticality ratings and dependency mapping.',
  },
  {
    icon: Layers,
    problem: 'Siloed Crisis Response',
    description: 'Multiple teams with conflicting procedures and no unified crisis management.',
    solution: 'Unified crisis management with role assignments, communication trees, and escalation paths.',
  },
];

// Key metrics
const dashboardMetrics = [
  { label: 'BCP Plans', value: '2', color: TEAL },
  { label: 'DR Plans', value: '2', color: '#f59e0b' },
  { label: 'Critical Coverage', value: '75%', color: '#ef4444' },
  { label: 'Last Test', value: '15d', color: '#3b82f6' },
];

// BCP Categories
const bcpCategories = [
  { name: 'Business Continuity Plans', icon: ClipboardList, desc: 'Operational continuity procedures', color: '#0d9488' },
  { name: 'Disaster Recovery Plans', icon: RefreshCw, desc: 'IT system recovery procedures', color: '#3b82f6' },
  { name: 'Crisis Management', icon: AlertTriangle, desc: 'Emergency response coordination', color: '#ef4444' },
  { name: 'Business Impact Analysis', icon: BarChart3, desc: 'Process criticality assessment', color: '#8b5cf6' },
  { name: 'Recovery Testing', icon: Activity, desc: 'Plan validation & exercises', color: '#f59e0b' },
  { name: 'Contact Trees', icon: Phone, desc: 'Emergency communication chains', color: '#10b981' },
];

// Capabilities
const capabilities = [
  {
    icon: ClipboardList,
    title: 'Business Continuity Planning',
    description: 'Document and maintain comprehensive business continuity plans with clear ownership and procedures.',
    features: ['Plan templates', 'Version control', 'Owner assignments', 'Review schedules'],
  },
  {
    icon: RefreshCw,
    title: 'Disaster Recovery',
    description: 'Define recovery strategies, RTO/RPO targets, and detailed recovery procedures for IT systems.',
    features: ['RTO/RPO tracking', 'Recovery procedures', 'Failover documentation', 'DR site management'],
  },
  {
    icon: BarChart3,
    title: 'Business Impact Analysis',
    description: 'Identify critical business functions, assess impact of disruptions, and prioritize recovery efforts.',
    features: ['Criticality ratings', 'Impact scoring', 'Dependency mapping', 'MAO calculations'],
  },
  {
    icon: Activity,
    title: 'Testing & Exercises',
    description: 'Schedule and document recovery tests, tabletop exercises, and full failover drills.',
    features: ['Test scheduling', 'Exercise tracking', 'Results documentation', 'Gap identification'],
  },
];

// Sample BCP data
const samplePlans = [
  {
    name: 'Corporate HQ - Hurricane Response',
    type: 'BCP',
    status: 'Active',
    statusColor: '#10b981',
    owner: 'John Smith',
    lastTest: '2025-10-15',
    rto: '4 hours',
    rpo: '1 hour',
  },
  {
    name: 'Data Center DR Plan',
    type: 'DRP',
    status: 'Active',
    statusColor: '#10b981',
    owner: 'Sarah Johnson',
    lastTest: '2025-11-20',
    rto: '2 hours',
    rpo: '15 min',
  },
  {
    name: 'Pandemic Response Plan',
    type: 'BCP',
    status: 'Review Due',
    statusColor: '#f59e0b',
    owner: 'Mike Wilson',
    lastTest: '2025-06-01',
    rto: '24 hours',
    rpo: '4 hours',
  },
];

// Recovery targets visualization
const recoveryTargets = [
  { system: 'Core Banking', rto: '1h', rpo: '15m', criticality: 'Critical', color: '#ef4444' },
  { system: 'Email Systems', rto: '4h', rpo: '1h', criticality: 'High', color: '#f59e0b' },
  { system: 'HR Portal', rto: '24h', rpo: '4h', criticality: 'Medium', color: '#3b82f6' },
  { system: 'Intranet', rto: '48h', rpo: '24h', criticality: 'Low', color: '#10b981' },
];

export function ResiliencePage() {
  const onNavigateToTop = useNavigateToTop();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-28" style={{ background: 'linear-gradient(135deg, #ccfbf1 0%, #99f6e4 50%, #5eead4 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute top-20 left-20"
            animate={{ rotate: [0, 10, -10, 0], y: [0, -15, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Shield className="w-8 h-8" style={{ color: `${TEAL}50` }} />
          </motion.div>

          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            className="absolute bottom-40 right-20"
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <RefreshCw className="w-6 h-6" style={{ color: `${TEAL}40` }} />
          </motion.div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -50 }}
            >
              <div
                style={{ backgroundColor: `${TEAL}15`, color: TEAL }}
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6"
              >
                <Shield className="w-4 h-4 mr-2" />
                BCRP
              </div>

              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Be Ready for Anything
              </h1>

              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                BOSSS BCRP provides comprehensive Business Continuity & Resiliency Planning
                capabilities—ensuring your organization can survive and
                recover from any disruption.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                {dashboardMetrics.map((metric, index) => (
                  <motion.div
                    key={index}
                    animate={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    className="bg-white/80 backdrop-blur-sm rounded-xl p-3 shadow-sm border border-gray-100"
                  >
                    <div className="text-2xl font-bold" style={{ color: metric.color }}>{metric.value}</div>
                    <div className="text-xs text-gray-500">{metric.label}</div>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  style={{ backgroundColor: TEAL }}
                  className="text-white px-8 py-3 rounded-lg transition-all hover:scale-105"
                  onClick={() => onNavigateToTop('/demo')}
                >
                  Schedule Demo
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>

                <Button
                  variant="outline"
                  style={{ borderColor: TEAL, color: TEAL }}
                  className="px-8 py-3 rounded-lg transition-all hover:scale-105"
                  onClick={() => onNavigateToTop('/contact')}
                >
                  Talk to Expert
                </Button>
              </div>
            </motion.div>

            {/* Plan Preview */}
            <motion.div
              className="relative"
              animate={{ opacity: 1, x: 0 }}
              initial={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                    <span className="ml-4 text-sm text-gray-500">BOSSS Resilience</span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="space-y-4">
                    {samplePlans.map((plan, index) => (
                      <motion.div
                        key={index}
                        animate={{ opacity: 1, x: 0 }}
                        initial={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                        className="bg-gray-50 rounded-lg p-4 border border-gray-100"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm">{plan.name}</h4>

                            <p className="text-xs text-gray-500">
                              Owner:
                              {plan.owner}
                            </p>
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-200 text-gray-700">{plan.type}</span>

                            <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: `${plan.statusColor}20`, color: plan.statusColor }}>
                              {plan.status}
                            </span>
                          </div>
                        </div>

                        <div className="flex gap-4 text-xs text-gray-500">
                          <span>
                            RTO:
                            <strong className="text-gray-700">{plan.rto}</strong>
                          </span>

                          <span>
                            RPO:
                            <strong className="text-gray-700">{plan.rpo}</strong>
                          </span>

                          <span>
                            Last Test:
                            <strong className="text-gray-700">{plan.lastTest}</strong>
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Answer-first overview (SEO-82) */}
      <AnswerSection path="/platform/resilience" />


      {/* Pain Points Section */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              When Disaster Strikes, Are You Ready?
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Most organizations discover their recovery gaps at the worst possible time.
              BOSSS Resilience ensures you&apos;re prepared before disruption hits.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {painPoints.map((point, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  style={{ borderLeftColor: TEAL }}
                  className="h-full hover:shadow-lg transition-shadow duration-300 border-l-4"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div
                        style={{ backgroundColor: '#fef2f2' }}
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      >
                        <point.icon className="w-6 h-6 text-red-500" />
                      </div>

                      <div>
                        <h3 className="font-bold text-gray-900 mb-2">{point.problem}</h3>
                        <p className="text-gray-500 text-sm mb-4">{point.description}</p>

                        <div className="flex items-start gap-2">
                          <CheckCircle style={{ color: TEAL }} className="w-5 h-5 flex-shrink-0 mt-0.5" />
                          <p className="text-sm" style={{ color: TEAL }}>{point.solution}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* RTO/RPO Visualization */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Define & Track Recovery Objectives
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Set clear RTO (Recovery Time Objective) and RPO (Recovery Point Objective) targets
              for every critical system, then track your ability to meet them.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* RTO/RPO Explainer */}
            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="w-6 h-6" style={{ color: TEAL }} />
                    <h3 className="font-bold text-gray-900">Recovery Time Objective (RTO)</h3>
                  </div>

                  <p className="text-gray-600 text-sm">
                    Maximum acceptable time a system can be offline after a disaster.
                    Example: Core banking must be restored within 1 hour.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <div className="flex items-center gap-3 mb-3">
                    <RefreshCw className="w-6 h-6 text-blue-600" />
                    <h3 className="font-bold text-gray-900">Recovery Point Objective (RPO)</h3>
                  </div>

                  <p className="text-gray-600 text-sm">
                    Maximum acceptable data loss measured in time.
                    Example: No more than 15 minutes of transactions can be lost.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-100">
                  <div className="flex items-center gap-3 mb-3">
                    <Activity className="w-6 h-6 text-amber-600" />
                    <h3 className="font-bold text-gray-900">Maximum Allowable Outage (MAO)</h3>
                  </div>

                  <p className="text-gray-600 text-sm">
                    Total time a business function can be unavailable before causing irreparable harm.
                    Helps prioritize recovery order.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Recovery Targets Table */}
            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <Card className="shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5" style={{ color: TEAL }} />
                    Recovery Targets by System
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  <div className="space-y-4">
                    {recoveryTargets.map((target, index) => (
                      <motion.div
                        key={index}
                        viewport={{ once: true }}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: target.color }} />

                          <div>
                            <p className="font-medium text-gray-900 text-sm">{target.system}</p>

                            <span
                              className="text-xs px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: `${target.color}20`, color: target.color }}
                            >
                              {target.criticality}
                            </span>
                          </div>
                        </div>

                        <div className="flex gap-6 text-sm">
                          <div className="text-center">
                            <p className="text-gray-500 text-xs">RTO</p>
                            <p className="font-bold text-gray-900">{target.rto}</p>
                          </div>

                          <div className="text-center">
                            <p className="text-gray-500 text-xs">RPO</p>
                            <p className="font-bold text-gray-900">{target.rpo}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Complete Resilience Capabilities
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to plan, test, and execute business continuity and disaster recovery.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-all duration-300 group">
                  <CardContent className="p-8">
                    <div
                      style={{ backgroundColor: `${TEAL}15` }}
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform"
                    >
                      <capability.icon className="w-7 h-7" style={{ color: TEAL }} />
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3">{capability.title}</h3>
                    <p className="text-gray-600 mb-6">{capability.description}</p>

                    <div className="flex flex-wrap gap-2">
                      {capability.features.map((feature, idx) => (
                        <span key={idx} className="text-xs px-3 py-1 rounded-full bg-gray-100 text-gray-700">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BCP Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Comprehensive Coverage
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              BOSSS Resilience covers every aspect of business continuity and disaster recovery.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {bcpCategories.map((category, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                className="text-center group"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <div
                  style={{ backgroundColor: `${category.color}15` }}
                  className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                >
                  <category.icon className="w-8 h-8" style={{ color: category.color }} />
                </div>

                <h3 className="font-semibold text-gray-900 text-sm mb-1">{category.name}</h3>
                <p className="text-xs text-gray-500">{category.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration with Other Modules */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Integrated with BOSSS Platform
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              BOSSS Resilience connects seamlessly with other modules for comprehensive security operations.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: 'GRC Integration',
                description: 'Link BCP/DR to compliance frameworks. Many standards (ISO 22301, SOC 2) require documented continuity plans.',
                color: '#8b5cf6',
              },
              {
                icon: Server,
                title: 'CMDB Connection',
                description: 'Map recovery plans to actual assets. Know exactly which systems support each business function.',
                color: '#3b82f6',
              },
              {
                icon: Users,
                title: 'IAM Coordination',
                description: 'Emergency access procedures integrated with identity management. Grant crisis access when needed.',
                color: '#f59e0b',
              },
            ].map((integration, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full text-center hover:shadow-lg transition-shadow">
                  <CardContent className="p-8">
                    <div
                      style={{ backgroundColor: `${integration.color}15` }}
                      className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6"
                    >
                      <integration.icon className="w-8 h-8" style={{ color: integration.color }} />
                    </div>

                    <h3 className="font-bold text-gray-900 mb-3">{integration.title}</h3>
                    <p className="text-gray-600 text-sm">{integration.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* See It In Action Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              See It In Action
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive business continuity and disaster recovery planning with
              RTO/RPO tracking, testing schedules, and crisis management workflows.
            </p>
          </motion.div>

          <ScreenshotGallery
            accentColor={TEAL}
            urlPath="resilience/bcp"
            screenshots={[
              { src: resilienceBcrpDashboard, label: 'Overview', alt: 'BOSSS business continuity and resiliency dashboard with testing compliance and RTO gap' },
              { src: resilienceContinuity, label: 'BCP Plans', alt: 'BOSSS business continuity plans with ownership and review dates' },
              { src: resilienceContinuityDetail, label: 'BCP Plan Detail', alt: 'BOSSS business continuity plan detail with strategies, dependencies, facilities, vendors, team, comms and test history' },
              { src: resilienceRecovery, label: 'DR Plans', alt: 'BOSSS IT disaster recovery plans covering critical systems' },
              { src: resilienceRecoveryDetail, label: 'DR Plan Detail', alt: 'BOSSS IT disaster recovery plan detail with RTO and RPO objectives, backups, failover sites, infrastructure and test history' },
            ]}
          />
        </div>
      </section>

      {/* AI Agent Banner */}
      <AIAgentBanner
        toolsCount={17}
        accentColor="#0d9488"
        agentName="Business Agent"
        description="Your AI-powered resilience copilot. Ask about BCPs, disaster recovery plans, RTO/RPO targets, or business impact analysis—get instant answers."
        exampleQueries={[
          '"Show me our disaster recovery status"',
          '"What systems have RTOs under 2 hours?"',
          '"List all critical business processes"',
          '"Which BCPs are due for review?"',
        ]}
      />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-cyan-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <Shield className="w-16 h-16 text-white/80 mx-auto mb-6" />

            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Be Prepared Before Disaster Strikes
            </h2>

            <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
              Schedule a demo to see how BOSSS Resilience can help you build, test, and maintain
              comprehensive business continuity and disaster recovery plans.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                className="bg-white text-teal-700 hover:bg-teal-50 px-8 py-3 rounded-lg transition-all hover:scale-105"
                onClick={() => onNavigateToTop('/demo')}
              >
                Schedule Demo
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>

              <Button
                className="border-2 border-white text-white bg-transparent hover:bg-white/10 px-8 py-3 rounded-lg transition-all"
                onClick={() => onNavigateToTop('/contact')}
              >
                Contact Sales
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default ResiliencePage;
